import qs from "qs";
import { OverlayScrollbars } from "overlayscrollbars";
import { generateUrl } from "@common/util";
import "overlayscrollbars/overlayscrollbars.css";
import "@styles/components/scrollbar.css";

const fn = {
    init: () => {
        fn.container = document.querySelector(".combined-search");
        if (!fn.container) {
            return;
        }

        fn.form = fn.container.closest("form");
        fn.input = fn.container.querySelector(".combined-search-input");
        fn.pillsContainer = fn.container.querySelector(".combined-search-pills");
        fn.valuesInput = fn.container.querySelector(".combined-search-values");

        fn.placeholderQuery = window.matchMedia("(min-width: 1024px)");
        fn.placeholderQuery.addEventListener("change", fn.setResponsivePlaceholder);
        fn.setResponsivePlaceholder();

        const categories = fn.parseJSON("categories");
        const subcategories = fn.parseJSON("subcategories");
        const thirdcategories = fn.parseJSON("thirdcategories");
        const practices = fn.parseJSON("practices");

        // Flatten main, sub and third categories into a single options list
        fn.categoryOptions = [];
        const seen = {};

        Object.entries(categories).forEach(([value, text]) => {
            if (seen[value]) {
                return;
            }
            seen[value] = true;
            fn.categoryOptions.push({ value, text });
        });

        Object.values(subcategories).forEach((subObj) => {
            if (typeof subObj !== "object" || subObj === null) {
                return;
            }
            Object.entries(subObj).forEach(([value, text]) => {
                if (seen[value]) {
                    return;
                }
                seen[value] = true;
                fn.categoryOptions.push({ value, text });
            });
        });

        Object.entries(thirdcategories).forEach(([value, text]) => {
            if (seen[value]) {
                return;
            }
            seen[value] = true;
            fn.categoryOptions.push({ value, text });
        });

        fn.categoryOptions.sort((a, b) => a.text.localeCompare(b.text));

        fn.practiceOptions = Object.entries(practices)
            .map(([id, data]) => ({
                value: id,
                text: data.name,
                city: data.city || ""
            }))
            .sort((a, b) => a.text.localeCompare(b.text));

        // Label lookup for building pills
        fn.labelMap = {};
        fn.categoryOptions.forEach((option) => (fn.labelMap[option.value] = option.text));

        // Prepopulate selected categories from the data-selected-items attributes
        fn.selected = [];
        ["categories", "subcategories"].forEach((elementId) => {
            const dataElement = document.getElementById(elementId);
            const selectedItems = dataElement ? dataElement.getAttribute("data-selected-items") : null;
            if (!selectedItems) {
                return;
            }
            selectedItems.split(",").forEach((value) => {
                value = value.trim();
                if (value && fn.labelMap[value] && !fn.selected.includes(value)) {
                    fn.selected.push(value);
                }
            });
        });

        fn.renderPills();
        fn.buildDropdown();
        fn.bindEvents();
    },

    setResponsivePlaceholder: () => {
        const mobilePlaceholder = fn.input.dataset.placeholderMobile;
        const desktopPlaceholder = fn.input.dataset.placeholderDesktop;

        if (!mobilePlaceholder || !desktopPlaceholder) {
            return;
        }

        fn.input.setAttribute("placeholder", fn.placeholderQuery.matches ? desktopPlaceholder : mobilePlaceholder);
    },

    parseJSON: (elementId) => {
        const dataElement = document.getElementById(elementId);
        if (!dataElement) {
            return {};
        }

        const dataJSON = dataElement.getAttribute("data-json");
        if (!dataJSON) {
            return {};
        }

        try {
            return JSON.parse(dataJSON.replace(/&quot;/g, '"').replace(/&#039;/g, "'"));
        } catch (e) {
            return {};
        }
    },

    buildDropdown: () => {
        fn.dropdown = document.createElement("div");
        fn.dropdown.className = "combined-search-dropdown force-hidden";
        fn.dropdown.innerHTML = `
            <div class="combined-search-columns">
                <div class="combined-search-col">
                    <h3 class="combined-search-heading">Specialty/Service Needed</h3>
                    <div class="combined-search-list" data-list="categories" data-scrollbar>
                        <div class="combined-search-list-inner"></div>
                    </div>
                </div>
                <div class="combined-search-col">
                    <h3 class="combined-search-heading">Organization / Practice</h3>
                    <div class="combined-search-list" data-list="practices" data-scrollbar>
                        <div class="combined-search-list-inner"></div>
                    </div>
                </div>
            </div>
        `;

        fn.container.parentElement.appendChild(fn.dropdown);

        fn.categoryList = fn.dropdown.querySelector('[data-list="categories"] .combined-search-list-inner');
        fn.practiceList = fn.dropdown.querySelector('[data-list="practices"] .combined-search-list-inner');
    },

    bindEvents: () => {
        fn.container.addEventListener("click", (e) => {
            if (e.target === fn.container) {
                fn.input.focus();
            }
        });

        fn.input.addEventListener("input", () => {
            fn.openDropdown();
            fn.renderDropdown();
        });

        fn.input.addEventListener("focus", () => {
            if (fn.input.value.trim().length > 0) {
                fn.openDropdown();
                fn.renderDropdown();
            }
        });

        fn.input.addEventListener("keydown", (e) => {
            if (e.key === "Escape") {
                fn.closeDropdown();
            }
        });

        document.addEventListener("click", (e) => {
            // If the target was detached by a re-render mid-click, ignore it
            if (e.target && !e.target.isConnected) {
                return;
            }
            if (!fn.container.contains(e.target)) {
                fn.closeDropdown();
            }
        });
    },

    openDropdown: () => {
        fn.dropdown.classList.remove("force-hidden");

        if (!fn.scrollbarsInitialized) {
            fn.scrollbarsInitialized = true;
            fn.dropdown.querySelectorAll("[data-scrollbar]").forEach((element) => {
                OverlayScrollbars(element, {
                    scrollbars: {
                        theme: "os-theme-custom",
                    },
                });
            });
        }
    },

    closeDropdown: () => {
        fn.dropdown.classList.add("force-hidden");
    },

    renderDropdown: () => {
        const query = fn.input.value.trim().toLowerCase();

        const categoryMatches = fn.categoryOptions
            .filter((option) => !fn.selected.includes(option.value) && option.text.toLowerCase().includes(query))
            .slice(0, 100);

        const practiceMatches = fn.practiceOptions
            .filter((option) => {
                const searchable = `${option.text} ${option.city}`.toLowerCase();
                return searchable.includes(query);
            })
            .slice(0, 100);

        fn.renderCategoryList(categoryMatches);
        fn.renderPracticeList(practiceMatches);
    },

    renderCategoryList: (matches) => {
        fn.categoryList.innerHTML = "";

        if (matches.length === 0) {
            fn.categoryList.appendChild(fn.buildNoResults());
            return;
        }

        matches.forEach((option) => {
            const button = document.createElement("button");
            button.type = "button";
            button.className = "combined-search-option";

            const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
            icon.setAttribute("viewBox", "0 0 16 16");
            icon.setAttribute("class", "combined-search-add-icon");
            icon.setAttribute("aria-hidden", "true");
            icon.innerHTML = '<circle cx="8" cy="8" r="8"></circle><path d="M8 4.5v7M4.5 8h7"></path>';

            const label = document.createElement("span");
            label.className = "combined-search-option-label";
            label.textContent = option.text;

            button.appendChild(icon);
            button.appendChild(label);

            // Keep focus in the search input so the dropdown stays open when selecting
            button.addEventListener("mousedown", (e) => {
                e.preventDefault();
            });
            button.addEventListener("click", () => fn.addCategory(option.value));

            fn.categoryList.appendChild(button);
        });
    },

    renderPracticeList: (matches) => {
        fn.practiceList.innerHTML = "";

        if (matches.length === 0) {
            fn.practiceList.appendChild(fn.buildNoResults());
            return;
        }

        matches.forEach((option) => {
            const link = document.createElement("a");
            link.className = "combined-search-practice";
            link.href = fn.buildPracticeURL(option);

            const name = document.createElement("span");
            name.className = "combined-search-practice-name";
            name.textContent = option.text;
            link.appendChild(name);

            if (option.city) {
                const city = document.createElement("span");
                city.className = "combined-search-practice-city";
                city.textContent = ` (${option.city})`;
                link.appendChild(city);
            }

            fn.practiceList.appendChild(link);
        });
    },

    buildNoResults: () => {
        const noResults = document.createElement("div");
        noResults.className = "combined-search-no-results";
        noResults.textContent = "No results found";
        return noResults;
    },

    buildPracticeURL: (option) => {
        const currentParams = qs.parse(window.location.search, { ignoreQueryPrefix: true });

        currentParams.practiceID = option.value;
        currentParams.page = "search";

        // Navigate to the specific practice; drop all other search filters
        delete currentParams.practice;
        delete currentParams.county;
        delete currentParams.service;
        delete currentParams.keyword;
        delete currentParams.subcategory;
        delete currentParams.relatedCategories;

        return generateUrl(fn.form, currentParams, true);
    },

    addCategory: (value) => {
        if (!fn.selected.includes(value)) {
            fn.selected.push(value);
        }

        fn.renderPills();
        fn.openDropdown();
        fn.renderDropdown();
        fn.input.focus();
    },

    removeCategory: (value) => {
        fn.selected = fn.selected.filter((selectedValue) => selectedValue !== value);

        fn.renderPills();

        if (!fn.dropdown.classList.contains("force-hidden")) {
            fn.renderDropdown();
        }
    },

    renderPills: () => {
        fn.pillsContainer.innerHTML = "";

        fn.selected.forEach((value) => {
            const label = fn.labelMap[value] || value;

            const pill = document.createElement("span");
            pill.className = "combined-search-pill";

            const pillLabel = document.createElement("span");
            pillLabel.textContent = label;

            const removeButton = document.createElement("button");
            removeButton.type = "button";
            removeButton.className = "combined-search-pill-remove";
            removeButton.setAttribute("aria-label", `Remove ${label}`);
            removeButton.innerHTML = "&times;";
            removeButton.addEventListener("click", () => fn.removeCategory(value));

            pill.appendChild(pillLabel);
            pill.appendChild(removeButton);

            fn.pillsContainer.appendChild(pill);
        });

        fn.valuesInput.value = fn.selected.join(",");
    },
};

export default {
    can: () => document.querySelectorAll(".combined-search").length > 0,
    run: fn.init,
};
